import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Container, Stack, Title, Text, Group, Badge, Image, Card, Button, SimpleGrid, Skeleton, Divider, Paper } from '@mantine/core';
import { IconMapPin, IconPhone, IconMail, IconHome, IconArrowLeft, IconExternalLink, IconCurrencyDollar } from '@tabler/icons-react';

const PropertyDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProperty();
  }, [id]);

  const fetchProperty = async () => {
    try {
      const response = await fetch(`/api/properties/${id}`);
      const data = await response.json();
      
      if (data.success) {
        setProperty(data.data);
      } else {
        console.error('Error fetching property:', data.message);
      }
    } catch (error) {
      console.error('Error fetching property:', error);
    } finally {
      setLoading(false);
    }
  };

  const getGoogleMapsUrl = (address, lat, lng) => {
    // Always include the full address for better accuracy
    // Zoom level 14 provides a good view with the marker centered and visible
    if (address) {
      const encodedAddress = encodeURIComponent(address);
      if (lat && lng) {
        // Include both address and coordinates, center on coordinates
        return `https://www.google.com/maps?q=${encodedAddress}&center=${lat},${lng}&zoom=14&output=embed`;
      }
      return `https://www.google.com/maps?q=${encodedAddress}&zoom=14&output=embed`;
    }
    // Fallback to coordinates only if no address
    if (lat && lng) {
      return `https://www.google.com/maps?q=${lat},${lng}&zoom=14&output=embed`;
    }
    return null;
  };

  const getGoogleMapsLink = (address, lat, lng) => {
    // Always include the full address for better accuracy
    if (address) {
      const encodedAddress = encodeURIComponent(address);
      if (lat && lng) {
        // Include both address and coordinates for best accuracy
        return `https://www.google.com/maps/search/?api=1&query=${encodedAddress}&ll=${lat},${lng}`;
      }
      return `https://www.google.com/maps/search/?api=1&query=${encodedAddress}`;
    }
    // Fallback to coordinates only if no address
    if (lat && lng) {
      return `https://www.google.com/maps?q=${lat},${lng}`;
    }
    return null;
  };

  if (loading) {
    return (
      <Container size="xl" py="md">
        <Stack gap="xl">
          <Skeleton height={400} />
          <Skeleton height={100} />
          <Skeleton height={200} />
        </Stack>
      </Container>
    );
  }

  if (!property) {
    return (
      <Container size="xl" py="md">
        <Stack gap="md" align="center">
          <Title order={2}>Property Not Found</Title>
          <Text c="dimmed">The property you're looking for doesn't exist.</Text>
          <Button component={Link} to="/properties" leftSection={<IconArrowLeft size={16} />}>
            Back to Properties
          </Button>
        </Stack>
      </Container>
    );
  }

  const mapUrl = getGoogleMapsUrl(
    `${property.address}, ${property.city}, ${property.state} ${property.zipCode}`,
    property.latitude,
    property.longitude
  );

  const mapLink = getGoogleMapsLink(
    `${property.address}, ${property.city}, ${property.state} ${property.zipCode}`,
    property.latitude,
    property.longitude
  );

  return (
    <Container size="xl" py="md">
      <Stack gap="xl">
        {/* Back Button */}
        <Button
          variant="light"
          leftSection={<IconArrowLeft size={16} />}
          onClick={() => navigate(-1)}
        >
          Back
        </Button>

        {/* Header Section */}
        <Card shadow="sm" padding="lg" radius="md" withBorder>
          <Stack gap="md">
            <Group justify="space-between" align="flex-start">
              <Stack gap="xs">
                <Group gap="md">
                  <Title order={1}>{property.title}</Title>
                  {property.featured && (
                    <Badge color="green" size="lg">Featured</Badge>
                  )}
                  <Badge color="green" size="lg">{property.propertyType}</Badge>
                  <Badge color="blue" size="lg">{property.listingType}</Badge>
                </Group>
                <Group gap="xs" c="dimmed">
                  <IconMapPin size={18} />
                  <Text size="lg">
                    {property.address}, {property.city}, {property.state} {property.zipCode}
                  </Text>
                </Group>
                {property.price && (
                  <Group gap="xs">
                    <IconCurrencyDollar size={24} color="green" />
                    <Text size="xl" fw={700} c="green">
                      ${property.price.toLocaleString()}
                    </Text>
                  </Group>
                )}
              </Stack>
            </Group>

            {/* Images */}
            {property.images && property.images.length > 0 && (
              <Image
                src={property.images[0]}
                height={400}
                radius="md"
                alt={property.title}
                style={{ objectFit: 'cover' }}
              />
            )}
          </Stack>
        </Card>

        {/* Map Section */}
        {(property.latitude && property.longitude) || property.address ? (
          <Card shadow="sm" padding="lg" radius="md" withBorder>
            <Stack gap="md">
              <Group justify="space-between" align="center">
                <Title order={3}>Location</Title>
                {mapLink && (
                  <Button
                    component="a"
                    href={mapLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="light"
                    rightSection={<IconExternalLink size={16} />}
                  >
                    Open in Google Maps
                  </Button>
                )}
              </Group>
              {mapUrl ? (
                <div style={{ width: '100%', height: '400px', borderRadius: '8px', overflow: 'hidden' }}>
                  <iframe
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    loading="lazy"
                    allowFullScreen
                    referrerPolicy="no-referrer-when-downgrade"
                    src={mapUrl}
                    title="Property Location Map"
                  />
                </div>
              ) : (
                <Paper p="xl" radius="md" withBorder>
                  <Stack align="center" gap="md">
                    <IconMapPin size={48} color="dimmed" />
                    <Text c="dimmed" ta="center">
                      Map unavailable. Address: {property.address}, {property.city}, {property.state}
                    </Text>
                  </Stack>
                </Paper>
              )}
            </Stack>
          </Card>
        ) : null}

        {/* Property Details Grid */}
        <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
          {/* Description */}
          {property.description && (
            <Card shadow="sm" padding="lg" radius="md" withBorder>
              <Title order={3} mb="md">Description</Title>
              <Text style={{ whiteSpace: 'pre-line' }}>{property.description}</Text>
            </Card>
          )}

          {/* Property Details */}
          <Card shadow="sm" padding="lg" radius="md" withBorder>
            <Title order={3} mb="md">Property Details</Title>
            <Stack gap="sm">
              {property.bedrooms && (
                <Group justify="space-between">
                  <Text c="dimmed">Bedrooms</Text>
                  <Text fw={500}>{property.bedrooms}</Text>
                </Group>
              )}
              {property.bathrooms && (
                <Group justify="space-between">
                  <Text c="dimmed">Bathrooms</Text>
                  <Text fw={500}>{property.bathrooms}</Text>
                </Group>
              )}
              {property.squareFeet && (
                <Group justify="space-between">
                  <Text c="dimmed">Square Feet</Text>
                  <Text fw={500}>{property.squareFeet.toLocaleString()}</Text>
                </Group>
              )}
              {property.lotSize && (
                <Group justify="space-between">
                  <Text c="dimmed">Lot Size</Text>
                  <Text fw={500}>
                    {property.lotSize.toLocaleString()} {property.lotSizeUnit || 'Acres'}
                  </Text>
                </Group>
              )}
              {property.yearBuilt && (
                <Group justify="space-between">
                  <Text c="dimmed">Year Built</Text>
                  <Text fw={500}>{property.yearBuilt}</Text>
                </Group>
              )}
              <Divider />
              {property.garageSpaces && (
                <Group justify="space-between">
                  <Text c="dimmed">Garage Spaces</Text>
                  <Text fw={500}>{property.garageSpaces}</Text>
                </Group>
              )}
              {property.parkingSpaces && (
                <Group justify="space-between">
                  <Text c="dimmed">Parking Spaces</Text>
                  <Text fw={500}>{property.parkingSpaces}</Text>
                </Group>
              )}
            </Stack>
          </Card>
        </SimpleGrid>

        {/* Features */}
        {(property.pool || property.hotTub || property.deck || property.patio || property.firepit || 
          property.outdoorKitchen || property.wildlifeViewing || property.hikingTrails || 
          property.hunting || property.fishing || property.atvTrails || property.horseProperty) && (
          <Card shadow="sm" padding="lg" radius="md" withBorder>
            <Title order={3} mb="md">Features</Title>
            <Group gap="md" wrap="wrap">
              {property.pool && <Badge color="blue" size="lg">Pool</Badge>}
              {property.hotTub && <Badge color="blue" size="lg">Hot Tub</Badge>}
              {property.deck && <Badge color="green" size="lg">Deck</Badge>}
              {property.patio && <Badge color="green" size="lg">Patio</Badge>}
              {property.firepit && <Badge color="orange" size="lg">Firepit</Badge>}
              {property.outdoorKitchen && <Badge color="orange" size="lg">Outdoor Kitchen</Badge>}
              {property.wildlifeViewing && <Badge color="green" size="lg">Wildlife Viewing</Badge>}
              {property.hikingTrails && <Badge color="green" size="lg">Hiking Trails</Badge>}
              {property.hunting && <Badge color="red" size="lg">Hunting</Badge>}
              {property.fishing && <Badge color="blue" size="lg">Fishing</Badge>}
              {property.atvTrails && <Badge color="orange" size="lg">ATV Trails</Badge>}
              {property.horseProperty && <Badge color="violet" size="lg">Horse Property</Badge>}
            </Group>
          </Card>
        )}

        {/* Utilities */}
        {(property.waterSource || property.sewer || property.electricity || property.internet || 
          property.heating || property.cooling) && (
          <Card shadow="sm" padding="lg" radius="md" withBorder>
            <Title order={3} mb="md">Utilities & Systems</Title>
            <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
              {property.waterSource && (
                <Group justify="space-between">
                  <Text c="dimmed">Water Source</Text>
                  <Text fw={500}>{property.waterSource}</Text>
                </Group>
              )}
              {property.sewer && (
                <Group justify="space-between">
                  <Text c="dimmed">Sewer</Text>
                  <Text fw={500}>{property.sewer}</Text>
                </Group>
              )}
              {property.electricity && (
                <Group justify="space-between">
                  <Text c="dimmed">Electricity</Text>
                  <Text fw={500}>{property.electricity}</Text>
                </Group>
              )}
              {property.internet && (
                <Group justify="space-between">
                  <Text c="dimmed">Internet</Text>
                  <Text fw={500}>{property.internet}</Text>
                </Group>
              )}
              {property.heating && (
                <Group justify="space-between">
                  <Text c="dimmed">Heating</Text>
                  <Text fw={500}>{property.heating}</Text>
                </Group>
              )}
              {property.cooling && (
                <Group justify="space-between">
                  <Text c="dimmed">Cooling</Text>
                  <Text fw={500}>{property.cooling}</Text>
                </Group>
              )}
            </SimpleGrid>
          </Card>
        )}

        {/* Contact Information */}
        {(property.listingAgent || property.listingAgentPhone || property.listingAgentEmail) && (
          <Card shadow="sm" padding="lg" radius="md" withBorder>
            <Title order={3} mb="md">Contact Information</Title>
            <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="md">
              {property.listingAgent && (
                <Group gap="xs">
                  <IconHome size={20} color="green" />
                  <Stack gap={0}>
                    <Text size="xs" c="dimmed">Listing Agent</Text>
                    <Text fw={500}>{property.listingAgent}</Text>
                  </Stack>
                </Group>
              )}
              {property.listingAgentPhone && (
                <Group gap="xs">
                  <IconPhone size={20} color="green" />
                  <Stack gap={0}>
                    <Text size="xs" c="dimmed">Phone</Text>
                    <Text fw={500}>{property.listingAgentPhone}</Text>
                  </Stack>
                </Group>
              )}
              {property.listingAgentEmail && (
                <Group gap="xs">
                  <IconMail size={20} color="green" />
                  <Stack gap={0}>
                    <Text size="xs" c="dimmed">Email</Text>
                    <Text fw={500}>{property.listingAgentEmail}</Text>
                  </Stack>
                </Group>
              )}
            </SimpleGrid>
          </Card>
        )}

        {/* Location Details */}
        <Card shadow="sm" padding="lg" radius="md" withBorder>
          <Title order={3} mb="md">Location Details</Title>
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
            <Stack gap="xs">
              <Text size="sm" c="dimmed">Address</Text>
              <Text fw={500}>{property.address}</Text>
            </Stack>
            <Stack gap="xs">
              <Text size="sm" c="dimmed">City</Text>
              <Text fw={500}>{property.city}</Text>
            </Stack>
            <Stack gap="xs">
              <Text size="sm" c="dimmed">State</Text>
              <Text fw={500}>{property.state}</Text>
            </Stack>
            <Stack gap="xs">
              <Text size="sm" c="dimmed">Zip Code</Text>
              <Text fw={500}>{property.zipCode}</Text>
            </Stack>
            {property.latitude && property.longitude && (
              <>
                <Stack gap="xs">
                  <Text size="sm" c="dimmed">Latitude</Text>
                  <Text fw={500}>{property.latitude}</Text>
                </Stack>
                <Stack gap="xs">
                  <Text size="sm" c="dimmed">Longitude</Text>
                  <Text fw={500}>{property.longitude}</Text>
                </Stack>
              </>
            )}
          </SimpleGrid>
        </Card>
      </Stack>
    </Container>
  );
};

export default PropertyDetail;

